import { Button, Snackbar, Tooltip } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import MuiAlert from '@material-ui/lab/Alert'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar,
} from '../../../../_metronic/_partials/controls'
import {
	deleteClub,
	getClubs,
	updateClub,
} from '../../../../api/club'
import { getContracts } from '../../../../api/contract'
import { FRONT_URL } from '../../../../utils'
import { alertError, alertSuccess } from '../../../../utils/logger'
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog'
import Table, {
	booleanFormatter,
	buttonsStyle,
	imageFormatter,
} from '../../../components/tables/table'

const euros = (centimos) =>
	new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(
		(centimos ?? 0) / 100
	)

/**
 * De los 48 clubes de la lista, la mayoría son CATÁLOGO: están cargados desde
 * antes de que ninguno supiera de SailRadar y no han contratado nada. Sin esta
 * columna, la única forma de saber cuáles pagan es abrir Contratos y buscarlos
 * uno a uno.
 *
 * Cuando un club tiene más de un contrato manda el más vivo: lo que se quiere
 * ver de un vistazo es si HOY es cliente, no su historial.
 */
const ORDEN = ['activo', 'impagado', 'pendiente', 'terminado']

function elQueManda(contratos) {
	return [...contratos].sort(
		(a, b) => ORDEN.indexOf(a.estado) - ORDEN.indexOf(b.estado)
	)[0]
}

function porClub(contratos) {
	const mapa = new Map()
	for (const c of contratos) {
		const id = String(c.club?._id ?? c.club ?? '')
		if (!id)
			continue
		mapa.set(id, (mapa.get(id) ?? []).concat(c))
	}
	return mapa
}

function getData(clubs, contratosPorClub) {
	let data = []
	for (let i = 0; i < clubs.length; ++i) {
		const elem = {}
		elem.logo = clubs[i].logo
		elem.name = clubs[i].fullName
		elem.createdAt = clubs[i].createdAt
		elem.default = clubs[i].default
		elem.contrato = contratosPorClub.get(String(clubs[i]._id))
			? elQueManda(contratosPorClub.get(String(clubs[i]._id)))
			: null
		elem.data = clubs[i]
		elem.id = clubs[i]._id
		data = data.concat(elem)
	}
	return data
}

export default function ClubsPage() {
	const [data, setData] = useState([])
	const [clubId, setClubId] = useState(null)
	const [selectedClub, setSelectedClub] = useState(null)
	const [openConfirmDialog, setOpenConfirmDialog] = useState(null)
	const [refresh, setRefresh] = useState(false)
	const [copiedLink, setCopiedLink] = useState(false)
	const history = useHistory()

	function buttonFormatter(cell) {
		const club = cell

		return (
			<>
				<Tooltip title="Edit">
					<Button
						style={buttonsStyle}
						size="small"
						onClick={() => history.push('/edit-club/' + cell._id)}
					>
						<EditIcon />
					</Button>
				</Tooltip>
				<Tooltip title="Delete">
					<Button
						style={buttonsStyle}
						size="small"
						onClick={() => {
							setClubId(cell._id)
							setOpenConfirmDialog(2)
						}}
					>
						<DeleteIcon />
					</Button>
				</Tooltip>
			</>
		)
	}

	function defaultFormatter(cell) {
		return cell ? <div className="default-label">default</div> : ''
	}

	/** Un color por estado: lo que se busca en esta lista es «¿cuáles pagan?». */
	function contratoFormatter(c) {
		if (!c)
			return <span style={{ color: '#98a4b0' }}>catálogo</span>

		const color = {
			activo: '#1b8a5a',
			impagado: '#c0392b',
			pendiente: '#8a6d1b',
			terminado: '#7b8794',
		}[c.estado]

		const detalle = c.pilot
			? 'piloto'
			: c.kind === 'regatta'
				? 'pack regata'
				: `${c.devices} aparatos · ${euros(c.monthlyCents)}/mes`

		return (
			<span style={{ whiteSpace: 'nowrap' }}>
				<strong style={{ color }}>{c.estado}</strong>
				<span style={{ color: '#7b8794' }}> · {detalle}</span>
			</span>
		)
	}

	const columns = [
		{ dataField: 'logo',
			text: '',
			formatter: imageFormatter,
			headerStyle: () => ({ width: '50px' }), },
		{ dataField: 'name', text: 'Full name', sort: true },
		{ dataField: 'contrato', text: 'Contrato', formatter: contratoFormatter },
		{
			dataField: 'data',
			text: '',
			formatter: buttonFormatter,
			align: 'end',
  			style: {  whiteSpace: 'nowrap' },
		},
	]

	useEffect(() => {
		// Los contratos aparte y sin bloquear: si esa llamada fallara, la lista
		// de clubes tiene que salir igual. Es lo que se venía usando y no puede
		// dejar de funcionar porque falle una columna añadida después.
		Promise.all([getClubs(), getContracts().catch(() => ({ data: [] }))])
			.then(([clubes, contratos]) => {
				if (clubes.status === 200) {
					setData(getData(clubes.data, porClub(contratos.data ?? [])))
					setRefresh(false)
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: 'Could not get clubs.',
				})
			})
	}, [refresh])

	return (
		<>
			<Card>
				<CardHeader title="Clubs list">
					<CardHeaderToolbar>
						<button
							type="button"
							className="btn btn-primary"
							onClick={() => history.push('/edit-club')}
						>
							Add new
						</button>
					</CardHeaderToolbar>
				</CardHeader>
				<CardBody>
					<Table data={data} columns={columns} />
					<ConfirmDialog
						title={'Are you sure you want to remove this club?'}
						open={openConfirmDialog === 2}
						setOpen={setOpenConfirmDialog}
						onConfirm={() => {
							deleteClub(clubId)
								.then((res) => {
									if (res.status === 204) {
										alertSuccess({
											title: 'Deleted!',
											customMessage:
												'Club removed successfully.',
										})
										setRefresh(true)
									}
								})
								.catch((error) => {
									alertError({
										error: error,
										customMessage: 'Could not remove club.',
									})
								})
						}}
					/>
					<ConfirmDialog
						title={`Are you sure you want to ${
							selectedClub?.active ? 'end' : 'start'
						} this club?`}
						open={openConfirmDialog === 4}
						setOpen={setOpenConfirmDialog}
						onConfirm={() => {
							updateClub(selectedClub._id, {
								...selectedClub,
								active: !selectedClub?.active,
							})
								.then((res) => {
									if (res.status === 200) {
										alertSuccess({
											title: 'Success!',
											customMessage: !selectedClub?.active
												? 'Club Started'
												: 'Club Ended',
										})
										setRefresh(true)
									}
								})
								.catch((error) => {
									alertError({
										error: error,
										customMessage:
											'Could not update the club.',
									})
								})
						}}
					></ConfirmDialog>
				</CardBody>
			</Card>
			<Snackbar
				open={copiedLink}
				autoHideDuration={6000}
				onClose={() => setCopiedLink(false)}
			>
				<MuiAlert severity="info" sx={{ width: '100%' }}>
					Club link copied
				</MuiAlert>
			</Snackbar>
		</>
	)
}
